import { useToast } from "../../../../components/common/toasts/ToastContext";
import ErrorToast from "../../../../components/common/toasts/ErrorToast";
import React, { useEffect, useId, useRef, useState } from "react";
import PremiumBackLink from "../PremiumBackLink";
import { premiumRequest } from "../../../../services/Premium";
import { premiumPaths } from "../../navigation";
import useAsyncAction from "../../hooks/useAsyncAction";
import ProgressBar from "../../../../components/common/ProgressBar";
import OrderActions from "../OrderActions";

function missingDocumentsMessage(files, requirements, documentOptions) {
  const invalid = files.find(
    ({ file, kind }) =>
      !documentOptions(file.name).some((option) => option.kind === kind),
  );
  if (invalid) {
    return `Wybierz rodzaj odpowiedni dla pliku „${invalid.file.name}” lub zastąp ten plik przed odczytem.`;
  }
  const selected = new Set(files.map((entry) => entry.kind));
  const missing = requirements.alternatives.map((group) =>
    group.filter((kind) => !selected.has(kind)),
  );
  if (missing.some((group) => !group.length)) return "";

  const labels = new Map(
    requirements.documents.map(({ kind, label }) => [kind, label]),
  );
  const options = missing.map((group) =>
    group.map((kind) => `plik typu „${labels.get(kind)}”`).join(" i "),
  );
  const hint = `Do odczytu dodaj ${options.join(" lub ")}.`;
  return files.length
    ? `${hint} Jeśli plik jest już na liście, zmień jego rodzaj.`
    : hint;
}

/** Przygotowuje komplet dokumentów i zapisuje zlecenie, zachowując klucz ponowienia. */
export default function OrderForm({
  organization,
  token,
  onCreated,
  onContinue = onCreated,
  order = null,
  onDirtyChange,
  onBusyChange,
  onOpenDocument,
}) {
  const { notify } = useToast();
  const hintId = useId();
  const requirements = organization.plugin?.requirements;
  const limits = requirements?.limits;
  const accept = [
    ...new Set(
      requirements?.documents.flatMap((item) => item.extensions) || [],
    ),
  ]
    .map((extension) => `.${extension}`)
    .join(",");
  const documentOptions = (name) =>
    requirements.documents.filter((item) =>
      item.extensions.includes(name.split(".").pop().toLowerCase()),
    );
  const [files, setFiles] = useState(() =>
    (order?.documents || []).map((doc) => ({
      id: doc.id,
      kind: doc.kind,
      file: { name: doc.name },
      existing: true,
    })),
  );
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [uploadProgress, setUploadProgress] = useState(0);
  const { busy, error, setError, run } = useAsyncAction(
    `${token}:${organization.id}:${order?.id || "new"}`,
  );
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const changeFiles = (next) => {
    setFiles(next);
    setRequestKey(crypto.randomUUID());
    onDirtyChange?.(true);
  };
  const addFiles = (fileList) => {
    if (busy) return;
    const added = Array.from(fileList || []);
    if (!added.length) return;
    const unsupported = added.find(
      (file) => !documentOptions(file.name).length,
    );
    if (unsupported) {
      notify(
        `Nieobsługiwany format pliku: ${unsupported.name}. Dozwolone rozszerzenia: ${accept}.`,
        "warning",
      );
      return;
    }
    const next = [
      ...files,
      ...added.map((file) => ({
        file,
        kind: documentOptions(file.name)[0]?.kind || "",
        id: crypto.randomUUID(),
      })),
    ];
    if (
      next.length > limits.files ||
      next.reduce((sum, entry) => sum + (entry.file.size || 0), 0) >
        limits.totalBytes ||
      added.some((file) => file.size > limits.fileBytes)
    ) {
      notify(
        `Dodaj maksymalnie ${limits.files} plików, do ${limits.fileBytes / 1024 / 1024} MB każdy i ${limits.totalBytes / 1024 / 1024} MB łącznie.`,
        "warning",
      );
    } else {
      changeFiles(next);
      setError("");
    }
  };
  const dragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !Array.from(event.dataTransfer.types).includes("Files")) return;
    dragDepth.current += 1;
    setDragging(true);
  };
  const dragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect =
      !busy && Array.from(event.dataTransfer.types).includes("Files")
        ? "copy"
        : "none";
  };
  const dragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const dropFiles = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    addFiles(event.dataTransfer.files);
  };
  const submit = (reread, onSaved = onCreated) => {
    if ((reread && readBlockReason) || !organization.active || busy) return;
    run(async (isActive) => {
      setUploadProgress(0);
      const data = new FormData();
      data.append("requestKey", requestKey);
      if (order) {
        data.append("revision", order.revision);
        data.append("reread", String(reread));
        data.append(
          "retained",
          JSON.stringify(
            files
              .filter((entry) => entry.existing)
              .map(({ id, kind }) => ({ id, kind })),
          ),
        );
      }
      files
        .filter((entry) => !entry.existing)
        .forEach(({ file, kind }) => {
          data.append("files", file);
          data.append("kinds", kind);
        });
      const result = await premiumRequest(
        token,
        order
          ? `/orders/${order.id}/documents`
          : `/organizations/${organization.id}/orders`,
        {
          method: order ? "PUT" : "POST",
          data,
          onUploadProgress: (event) => {
            if (isActive())
              setUploadProgress(
                event.total > 0 ? (event.loaded / event.total) * 100 : null,
              );
          },
        },
      );
      if (isActive()) {
        onDirtyChange?.(false);
        onSaved(result.order);
      }
    });
  };
  if (!requirements)
    return (
      <ErrorToast message="Nie można wczytać konfiguracji dokumentów firmy. Odśwież stronę." />
    );
  const readBlockReason = organization.active
    ? missingDocumentsMessage(files, requirements, documentOptions)
    : "Firma jest nieaktywna. Zapisywanie i odczyt dokumentów są niedostępne.";
  const canContinue =
    !busy && organization.active && (order ? files.length > 0 : !readBlockReason);
  const cancelTo = premiumPaths.orders(organization.id);
  return (
    <>
      <form
        className="card premium-panel"
        onSubmit={(event) => {
          event.preventDefault();
          submit(!order);
        }}
      >
        <h2>
          {order
            ? "Zmień dokumenty źródłowe"
            : "Dodaj dokumenty jednej dostawy"}
        </h2>
        <p className="text-muted">
          {order
            ? "Usuń błędne pliki, dodaj nowe lub zmień ich rodzaj, a następnie zapisz zmiany. Ponowny odczyt zastąpi dane formularza danymi z dokumentów. "
            : "Dokumenty utworzą projekt jednego świadectwa. Możesz sprawdzić dane i podgląd PDF bez wystawiania. Wystawienie wymaga osobnego potwierdzenia. "}
          {requirements.hint}
        </p>
        <ErrorToast message={error} />
        {busy && (
          <ProgressBar
            label={
              uploadProgress >= 100
                ? order
                  ? "Zapisywanie dokumentów"
                  : "Zapisywanie zlecenia"
                : "Wysyłanie dokumentów"
            }
            value={uploadProgress >= 100 ? undefined : uploadProgress}
            description={
              uploadProgress >= 100
                ? "Pliki zostały wysłane. Czekamy na zapis zlecenia."
                : "Czekamy na zapis dokumentów."
            }
          />
        )}
        <fieldset disabled={busy}>
          <label
            className={`premium-dropzone${dragging && !busy ? " premium-dropzone-dragging" : ""}`}
            aria-disabled={busy}
            onDragEnter={dragEnter}
            onDragOver={dragOver}
            onDragLeave={dragLeave}
            onDrop={dropFiles}
          >
            {requirements.uploadLabel}
            <input
              type="file"
              multiple
              accept={accept}
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
              className="form-control premium-file-input"
            />
            <small>Przeciągnij pliki tutaj lub wybierz je z dysku.</small>
            <small>
              Do {limits.files} plików · {limits.fileBytes / 1024 / 1024} MB na
              plik
            </small>
          </label>
          <ul className="premium-files">
            {files.map((entry, index) => (
              <li key={entry.id}>
                <span>
                  {entry.file.name}{" "}
                  {!entry.existing && (
                    <small>({Math.ceil(entry.file.size / 1024)} KB)</small>
                  )}
                </span>
                <select
                  aria-label={`Rodzaj pliku ${entry.file.name}`}
                  className="form-select"
                  value={entry.kind}
                  onChange={(event) =>
                    changeFiles(
                      files.map((item, i) =>
                        i === index
                          ? { ...item, kind: event.target.value }
                          : item,
                      ),
                    )
                  }
                >
                  {documentOptions(entry.file.name).map((option) => (
                    <option key={option.kind} value={option.kind}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {entry.existing && onOpenDocument && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => onOpenDocument(entry.id)}
                  >
                    Otwórz dokument
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  aria-label={`Usuń ${entry.file.name}`}
                  onClick={() =>
                    changeFiles(files.filter((_, i) => i !== index))
                  }
                >
                  Usuń
                </button>
              </li>
            ))}
          </ul>
          {(order || !canContinue) && (
            <div className="premium-form-actions premium-document-actions">
              {!order && !canContinue && (
                <PremiumBackLink to={cancelTo} destination="Lista świadectw" />
              )}
              <p
                id={hintId}
                role="status"
                className={
                  readBlockReason ? "premium-document-hint" : "visually-hidden"
                }
              >
                {readBlockReason && (
                  <i className="bi bi-info-circle" aria-hidden="true" />
                )}
                {readBlockReason}
              </p>
              {order && !files.length && organization.active && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => submit(false)}
                >
                  Zapisz wersję roboczą
                </button>
              )}
              {order && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={Boolean(readBlockReason)}
                  aria-describedby={readBlockReason ? hintId : undefined}
                  onClick={() => submit(true)}
                >
                  Zapisz i odczytaj dokumenty
                </button>
              )}
            </div>
          )}
        </fieldset>
      </form>
      {canContinue && (
        <OrderActions
          backTo={order ? undefined : cancelTo}
          backLabel="Lista świadectw"
          save={() => submit(!order)}
          onNext={() => submit(!order, onContinue)}
        />
      )}
    </>
  );
}
